import portfolioImage from "../assets/portfolio-zs.svg";
import TechHero from "../components/TechHero";

export default function Portfolio() {
  return (
    <div className="space-y-12">
      <TechHero
        kicker="Showcase"
        title="Launch-ready products with premium polish"
        subtitle="Design, development, and deployment executed with precision for modern IT brands."
        ctaText="Request a demo"
      />
      <section className="animate-3d-entrance">
        <div className="grid items-center gap-8 lg:grid-cols-[0.9fr_1.1fr]">
          <div className="order-2 lg:order-1 animate-slide-in-right animate-3d-hover-continuous">
            <p className="home-tagline">
              <span className="brand-highlight">ZS NEXATECH</span> Portfolio
            </p>
            <div className="page-title">
              <h1 className="mt-3 text-4xl font-bold text-white">Our capabilities & expertise</h1>
            </div>
            <p className="mt-4 max-w-2xl text-gray-100">
              Sample builds highlighting how we combine strategy, UX, and engineering to deliver premium outcomes.
            </p>
          </div>
          <div className="order-1 flex justify-center lg:order-2 animate-3d-rotate-in animate-3d-rotate-continuous">
              <img
                src={portfolioImage}
                alt="ZS NEXATECH portfolio overview"
                className="w-full max-w-md rounded-3xl border border-white/30 bg-white/10 backdrop-blur-lg p-4 shadow-lg hover:shadow-2xl transition-all animate-3d-float"
                loading="lazy"
              />
            </div>
          </div>
      </section>

      <section className="card-grid grid gap-6 lg:grid-cols-3">
        {[
          {
            title: "Education CRM Platform",
            type: "Education",
            desc: "Student management system with enrollment tracking, attendance, automated fee reminders, and parent communication portal.",
            tags: ["CRM", "Dashboard", "Automation"],
            image: "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=800&h=600&fit=crop&q=80",
            metrics: { performance: "95", loadTime: "1.2s", users: "500+" }
          },
          {
            title: "Healthcare Management Portal",
            type: "Healthcare",
            desc: "Complete clinic solution with appointment scheduling, patient records, prescription management, and billing integration.",
            tags: ["Web App", "Scheduling", "Reports"],
            image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&h=600&fit=crop&q=80",
            metrics: { performance: "98", loadTime: "0.9s", uptime: "99.9%" }
          },
          {
            title: "Restaurant Digital Platform",
            type: "Restaurant",
            desc: "Modern restaurant website with digital menu, QR code ordering, online payments, and customer loyalty program.",
            tags: ["Website", "Payments", "Loyalty"],
            image: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800&h=600&fit=crop&q=80",
            metrics: { performance: "92", conversion: "+45%", orders: "1000+" }
          },
          {
            title: "Salon & Spa Management",
            type: "Wellness",
            desc: "Booking system with calendar management, staff scheduling, service packages, and automated reminder notifications.",
            tags: ["Mobile App", "Bookings"],
            image: "https://images.unsplash.com/photo-1560066984-138dadb4c035?w=800&h=600&fit=crop&q=80",
            metrics: { performance: "96", bookings: "+60%", retention: "85%" }
          },
          {
            title: "Real Estate Platform",
            type: "Real Estate",
            desc: "Property listing portal with advanced search, lead capture forms, virtual tours, and broker analytics dashboard.",
            tags: ["Website", "Leads", "Analytics"],
            image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&h=600&fit=crop&q=80",
            metrics: { performance: "94", leads: "+72%", listings: "800+" }
          },
          {
            title: "SaaS MVP Development",
            type: "SaaS",
            desc: "Rapid MVP development with user authentication, subscription billing, admin dashboard, and API integrations.",
            tags: ["SaaS", "MVP", "Admin"],
            image: "https://images.unsplash.com/photo-1551650975-87deedd944c3?w=800&h=600&fit=crop&q=80",
            metrics: { performance: "97", signups: "+120%", loadTime: "0.8s" }
          },
          {
            title: "HRMS Solution",
            type: "HRMS",
            desc: "HR management system with employee onboarding, biometric attendance, leave management, and payroll-ready reports.",
            tags: ["HRMS", "Workflows", "Compliance"],
            image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&h=600&fit=crop&q=80",
            metrics: { performance: "93", efficiency: "+50%", employees: "200+" }
          },
          {
            title: "Automobile Dealership System",
            type: "Automobile",
            desc: "Comprehensive dealer platform with vehicle inventory, service scheduling, customer CRM, and sales tracking.",
            tags: ["Web App", "Inventory", "Leads"],
            image: "https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=800&h=600&fit=crop&q=80",
            metrics: { performance: "95", sales: "+38%", vehicles: "300+" }
          },
        ].map((project) => (
          <div
            key={project.title}
            className="card rounded-2xl border border-white/30 bg-white/10 backdrop-blur-lg overflow-hidden shadow-sm hover:shadow-2xl hover:scale-105 hover:-translate-y-3 hover:bg-white/20 transition-all duration-500 group"
          >
            <div className="card-image-wrapper">
              <img 
                src={project.image} 
                alt={project.title}
                className="card-image group-hover:scale-125"
                loading="lazy"
              />
              <div className="card-image-overlay group-hover:opacity-100">
                <span className="text-sm font-bold text-white uppercase tracking-wide transform group-hover:scale-110 transition-transform">➡️ View Details</span>
              </div>
            </div>
            <div className="p-6">
              <p className="text-xs font-semibold uppercase tracking-widest text-red-200">
                {project.type}
              </p>
              <h3 className="mt-2 text-lg font-semibold text-white">{project.title}</h3>
              <p className="mt-2 text-sm text-gray-100">{project.desc}</p>
              
              {/* Performance Metrics */}
              <div className="mt-4 grid grid-cols-3 gap-2 p-3 rounded-lg bg-linear-to-r from-amber-500/10 to-orange-500/10 border border-amber-400/20">
                {Object.entries(project.metrics).map(([key, value]) => (
                  <div key={key} className="text-center">
                    <div className="text-lg font-black text-amber-300">{value}</div>
                    <div className="text-xs text-white/70 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</div>
                  </div>
                ))}
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
              {project.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full border border-white/30 bg-white/10 px-3 py-1 text-xs font-medium text-white backdrop-blur-md"
                >
                  {tag}
                </span>
              ))}
              </div>
            </div>
          </div>
        ))}
      </section>

      <section className="premium-panel glass-panel animated-border">
        <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr] items-center">
          <div>
            <span className="section-kicker holo-gradient text-slate-900">Spotlight</span>
            <h2 className="mt-4 text-3xl font-bold text-white">Conversion‑first commerce rebuild</h2>
            <p className="mt-3 text-gray-100">
              A retail brand needed a faster, cleaner shopping experience. We delivered a new UX, improved performance, and integrated analytics to track funnel drop‑offs.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              {[
                "Performance budgets",
                "UX re‑architecture",
                "Conversion tracking",
                "CRM integration",
              ].map((item) => (
                <span key={item} className="icon-pill">✅ {item}</span>
              ))}
            </div>
          </div>
          <div className="grid gap-4">
            {[
              { title: "Discovery", desc: "Customer journey mapping + analytics audit." },
              { title: "Build", desc: "New UI kit, responsive layouts, and optimized checkout." },
              { title: "Launch", desc: "Monitoring + post‑launch performance tuning." },
            ].map((step) => (
              <div key={step.title} className="card rounded-2xl border border-white/30 bg-white/10 backdrop-blur-lg p-5">
                <h3 className="text-lg font-semibold text-white">{step.title}</h3>
                <p className="mt-2 text-sm text-gray-100">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div className="card rounded-2xl border border-white/30 bg-white/10 backdrop-blur-lg p-6 hover:shadow-2xl hover:scale-105 hover:-translate-y-2 hover:bg-white/20 transition-all duration-300 group relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none bg-linear-to-br from-amber-500/0 to-rose-600/0 group-hover:from-amber-500/10 group-hover:to-rose-600/10 transition-all duration-500"></div>
          <div className="relative z-10">
            <div className="text-4xl mb-3 transform group-hover:scale-125 group-hover:rotate-6 transition-all duration-500">💼</div>
            <h2 className="text-2xl font-bold text-white">💼 Project types</h2>
            <p className="mt-2 text-sm text-gray-100">
              We deliver products of all sizes, from quick MVPs to enterprise systems.
            </p>
            <ul className="mt-4 space-y-2 text-sm text-gray-100">
              <li className="flex items-start"><span className="text-amber-400 mr-2 mt-0.5">•</span><span>Startup MVPs and product launches</span></li>
              <li className="flex items-start"><span className="text-amber-400 mr-2 mt-0.5">•</span><span>Company websites and brand refresh</span></li>
              <li className="flex items-start"><span className="text-amber-400 mr-2 mt-0.5">•</span><span>Internal tools and workflow automation</span></li>
              <li className="flex items-start"><span className="text-amber-400 mr-2 mt-0.5">•</span><span>Mobile apps for customers and teams</span></li>
            </ul>
          </div>
        </div>
        <div className="card rounded-2xl border border-white/30 bg-white/10 backdrop-blur-lg p-6 hover:shadow-2xl hover:scale-105 hover:-translate-y-2 hover:bg-white/20 transition-all duration-300 group relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none bg-linear-to-br from-purple-500/0 to-pink-600/0 group-hover:from-purple-500/10 group-hover:to-pink-600/10 transition-all duration-500"></div>
          <div className="relative z-10">
            <div className="text-4xl mb-3 transform group-hover:scale-125 group-hover:rotate-6 transition-all duration-500">🛠️</div>
            <h2 className="text-2xl font-bold text-white">🛠️ Technology & performance</h2>
            <p className="mt-2 text-sm text-gray-100">
              Production-tested stack delivering 99.9% uptime across all projects.
            </p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {[
                { name: "React 18 + TypeScript", icon: "⚛️" },
                { name: "Java Spring Boot 3.x", icon: "☕" },
                { name: "MySQL 8.x + PostgreSQL", icon: "🗄️" },
                { name: "RESTful APIs + GraphQL", icon: "🔗" },
                { name: "AWS / Azure Cloud", icon: "☁️" },
                { name: "Real-time Analytics", icon: "📊" },
              ].map((item) => (
                <div
                  key={item.name}
                  className="card rounded-xl border border-white/30 bg-white/10 backdrop-blur-md px-3 py-2 text-xs text-white/90 hover:bg-white/20 hover:border-white/40 hover:scale-105 transition-all duration-300 flex items-center gap-2"
                >
                  <span className="text-sm">{item.icon}</span>
                  <span>{item.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="card rounded-3xl border border-white/30 bg-white/10 backdrop-blur-lg p-8 hover:shadow-2xl hover:bg-white/20 transition-all duration-300">
        <h2 className="text-3xl font-bold text-white">👀 Ready to build your project?</h2>
        <p className="mt-2 max-w-2xl text-gray-100">
          Share your goals and constraints. We’ll craft a clear plan, timeline, and delivery approach.
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          <a href="/contact" className="btn-gold">Request portfolio</a>
          <a className="link-gold" href="/contact">
            Contact us
          </a>
        </div>
      </section>
    </div>
  );
}
